// Professional Transform Gizmo Component for Scene Editor
import * as PIXI from 'pixi.js';
import { logger } from '@/engine/logging';
import type { SceneEditorMode } from '../types';

export interface TransformGizmoConfig {
  axisLength: number;
  axisWidth: number;
  handleSize: number;
  scaleBoxSize: number;
}

export class TransformGizmo {
  private container: PIXI.Container;
  private stage: PIXI.Container;
  private viewport: PIXI.Container;
  private config: TransformGizmoConfig;
  private mode: SceneEditorMode = 'select';

  // Visual groups for different modes
  private moveGizmo?: PIXI.Container;
  private scaleGizmo?: PIXI.Container;
  private rotateGizmo?: PIXI.Container;

  // Interaction state
  private isDragging = false;
  private dragAxis: 'x' | 'y' | 'xy' | 'rotate' | null = null;
  private dragStartPos = { x: 0, y: 0 };

  // Callbacks
  private onTransform?: (type: 'move' | 'scale' | 'rotate', data: any) => void;

  // Colors (Unity-like)
  private readonly COLOR_X = 0xff0000; // Red
  private readonly COLOR_Y = 0x00ff00; // Green
  private readonly COLOR_Z = 0x0088ff; // Blue (for center/universal)
  private readonly COLOR_HOVER = 0xffff00; // Yellow

  constructor(
    container: PIXI.Container,
    stage: PIXI.Container,
    viewport: PIXI.Container,
    config?: Partial<TransformGizmoConfig>
  ) {
    this.container = container;
    this.stage = stage;
    this.viewport = viewport;
    this.config = {
      axisLength: 60,
      axisWidth: 3,
      handleSize: 12,
      scaleBoxSize: 10,
      ...config,
    };

    this.setupInteraction();
  }

  setMode(mode: SceneEditorMode) {
    this.mode = mode;
    this.rebuild();
  }

  private updateVisibility() {
    // Show/hide gizmos based on mode
    if (this.moveGizmo) this.moveGizmo.visible = this.mode === 'move';
    if (this.scaleGizmo) this.scaleGizmo.visible = this.mode === 'scale';
    if (this.rotateGizmo) this.rotateGizmo.visible = this.mode === 'rotate';
  }

  setOnTransform(callback: (type: 'move' | 'scale' | 'rotate', data: any) => void) {
    this.onTransform = callback;
  }

  show(object: {
    x: number;
    y: number;
    scale: number;
    rotation: number;
    width?: number;
    height?: number;
  }) {
    // Don't rebuild during drag
    if (this.isDragging) {
      this.container.position.set(object.x, object.y);
      return;
    }

    this.container.position.set(object.x, object.y);
    this.container.rotation = 0; // Gizmo always aligned to world
    this.rebuild();
    this.container.visible = true;
  }

  hide() {
    if (this.isDragging) return;
    this.container.visible = false;
  }

  updatePosition(x: number, y: number) {
    this.container.position.set(x, y);
  }

  private rebuild() {
    this.clearGizmo();

    switch (this.mode) {
      case 'move':
        this.createMoveGizmo();
        break;
      case 'scale':
        this.createScaleGizmo();
        break;
      case 'rotate':
        this.createRotateGizmo();
        break;
      case 'select':
        this.createSelectGizmo();
        break;
    }

    this.updateVisibility();
  }

  private clearGizmo() {
    // Очищаем все дочерние элементы контейнера
    this.container.removeChildren();

    // Очищаем ссылки
    this.moveGizmo = undefined;
    this.scaleGizmo = undefined;
    this.rotateGizmo = undefined;
  }

  // ==========================================
  // MOVE GIZMO (Arrows on X and Y axes)
  // ==========================================
  private createMoveGizmo() {
    this.moveGizmo = new PIXI.Container();
    this.container.addChild(this.moveGizmo);

    // X Axis (Red Arrow - Right)
    const xAxis = this.createArrow(this.COLOR_X, 0);
    xAxis.eventMode = 'static';
    xAxis.cursor = 'ew-resize';
    xAxis.on('pointerdown', e => this.startDrag('x', e));
    xAxis.on('pointerover', () => this.highlightAxis(xAxis, this.COLOR_HOVER));
    xAxis.on('pointerout', () => this.highlightAxis(xAxis, this.COLOR_X));
    this.moveGizmo.addChild(xAxis);

    // Y Axis (Green Arrow - Up)
    const yAxis = this.createArrow(this.COLOR_Y, -Math.PI / 2);
    yAxis.eventMode = 'static';
    yAxis.cursor = 'ns-resize';
    yAxis.on('pointerdown', e => this.startDrag('y', e));
    yAxis.on('pointerover', () => this.highlightAxis(yAxis, this.COLOR_HOVER));
    yAxis.on('pointerout', () => this.highlightAxis(yAxis, this.COLOR_Y));
    this.moveGizmo.addChild(yAxis);

    // Center Handle (XY movement)
    const centerHandle = new PIXI.Graphics();
    centerHandle.circle(0, 0, this.config.handleSize);
    centerHandle.fill({ color: this.COLOR_Z, alpha: 0.5 });
    centerHandle.stroke({ color: 0xffffff, width: 2 });
    centerHandle.eventMode = 'static';
    centerHandle.cursor = 'move';
    centerHandle.on('pointerdown', e => this.startDrag('xy', e));
    centerHandle.on('pointerover', () => {
      centerHandle.clear();
      centerHandle.circle(0, 0, this.config.handleSize);
      centerHandle.fill({ color: this.COLOR_HOVER, alpha: 0.7 });
      centerHandle.stroke({ color: 0xffffff, width: 2 });
    });
    centerHandle.on('pointerout', () => {
      centerHandle.clear();
      centerHandle.circle(0, 0, this.config.handleSize);
      centerHandle.fill({ color: this.COLOR_Z, alpha: 0.5 });
      centerHandle.stroke({ color: 0xffffff, width: 2 });
    });
    this.moveGizmo.addChild(centerHandle);
  }

  private createArrow(color: number, rotation: number): PIXI.Container {
    const arrow = new PIXI.Container();
    arrow.rotation = rotation;

    const graphics = new PIXI.Graphics();

    // Arrow line
    graphics.moveTo(0, 0);
    graphics.lineTo(this.config.axisLength, 0);
    graphics.stroke({ color, width: this.config.axisWidth });

    // Arrow head (triangle)
    const headSize = this.config.handleSize;
    graphics.moveTo(this.config.axisLength, 0);
    graphics.lineTo(this.config.axisLength - headSize, -headSize / 2);
    graphics.lineTo(this.config.axisLength - headSize, headSize / 2);
    graphics.lineTo(this.config.axisLength, 0);
    graphics.fill({ color });

    arrow.addChild(graphics);
    return arrow;
  }

  // ==========================================
  // SCALE GIZMO (Boxes on corners and axes)
  // ==========================================
  private createScaleGizmo() {
    this.scaleGizmo = new PIXI.Container();
    this.container.addChild(this.scaleGizmo);

    const len = this.config.axisLength;

    // X Axis (Red Line + Box)
    const xLine = new PIXI.Graphics();
    xLine.moveTo(0, 0);
    xLine.lineTo(len, 0);
    xLine.stroke({ color: this.COLOR_X, width: 2 });
    this.scaleGizmo.addChild(xLine);

    const xBox = this.createScaleBox(this.COLOR_X, len, 0);
    xBox.eventMode = 'static';
    xBox.cursor = 'ew-resize';
    xBox.on('pointerdown', e => this.startDrag('x', e));
    this.scaleGizmo.addChild(xBox);

    // Y Axis (Green Line + Box)
    const yLine = new PIXI.Graphics();
    yLine.moveTo(0, 0);
    yLine.lineTo(0, -len);
    yLine.stroke({ color: this.COLOR_Y, width: 2 });
    this.scaleGizmo.addChild(yLine);

    const yBox = this.createScaleBox(this.COLOR_Y, 0, -len);
    yBox.eventMode = 'static';
    yBox.cursor = 'ns-resize';
    yBox.on('pointerdown', e => this.startDrag('y', e));
    this.scaleGizmo.addChild(yBox);

    // Uniform Scale Box (Blue)
    const uniformBox = this.createScaleBox(this.COLOR_Z, 0, 0);
    uniformBox.eventMode = 'static';
    uniformBox.cursor = 'nwse-resize';
    uniformBox.on('pointerdown', e => this.startDrag('xy', e));
    this.scaleGizmo.addChild(uniformBox);
  }

  private createScaleBox(color: number, x: number, y: number): PIXI.Graphics {
    const box = new PIXI.Graphics();
    const size = this.config.scaleBoxSize;
    box.rect(-size / 2, -size / 2, size, size);
    box.fill({ color });
    box.stroke({ color: 0xffffff, width: 2 });
    box.position.set(x, y);
    return box;
  }

  // ==========================================
  // ROTATE GIZMO (Circle with handle)
  // ==========================================
  private createRotateGizmo() {
    this.rotateGizmo = new PIXI.Container();
    this.container.addChild(this.rotateGizmo);

    const radius = this.config.axisLength * 0.8;

    // Circle
    const circle = new PIXI.Graphics();
    circle.circle(0, 0, radius);
    circle.stroke({ color: this.COLOR_Z, width: 2 });
    this.rotateGizmo.addChild(circle);

    // Rotate handle at top
    const handle = new PIXI.Graphics();
    handle.circle(0, -radius, this.config.handleSize);
    handle.fill({ color: this.COLOR_Z });
    handle.stroke({ color: 0xffffff, width: 2 });
    handle.eventMode = 'static';
    handle.cursor = 'grab';
    handle.on('pointerdown', e => this.startDrag('rotate', e));
    handle.on('pointerover', () => {
      handle.clear();
      handle.circle(0, -radius, this.config.handleSize);
      handle.fill({ color: this.COLOR_HOVER });
      handle.stroke({ color: 0xffffff, width: 2 });
    });
    handle.on('pointerout', () => {
      handle.clear();
      handle.circle(0, -radius, this.config.handleSize);
      handle.fill({ color: this.COLOR_Z });
      handle.stroke({ color: 0xffffff, width: 2 });
    });
    this.rotateGizmo.addChild(handle);
  }

  // ==========================================
  // SELECT GIZMO (Bounding box with corner handles)
  // ==========================================
  private createSelectGizmo() {
    // Create corner handles for select mode
    const positions = [
      { x: -this.config.axisLength / 2, y: -this.config.axisLength / 2, type: 'nw' },
      { x: this.config.axisLength / 2, y: -this.config.axisLength / 2, type: 'ne' },
      { x: this.config.axisLength / 2, y: this.config.axisLength / 2, type: 'se' },
      { x: -this.config.axisLength / 2, y: this.config.axisLength / 2, type: 'sw' },
    ];

    positions.forEach(pos => {
      const handle = this.createSelectHandle(pos.x, pos.y, pos.type);
      this.container.addChild(handle);
    });

    // Create bounding box lines
    this.createBoundingBoxLines();
  }

  private createSelectHandle(x: number, y: number, type: string): PIXI.Graphics {
    const handle = new PIXI.Graphics();
    handle.rect(
      -this.config.handleSize / 2,
      -this.config.handleSize / 2,
      this.config.handleSize,
      this.config.handleSize
    );
    handle.fill({ color: this.COLOR_Z, alpha: 0.5 });
    handle.stroke({ color: 0xffffff, width: 2 });
    handle.position.set(x, y);
    handle.eventMode = 'static';

    // Set cursor based on corner position
    if (type === 'nw' || type === 'se') {
      handle.cursor = 'nwse-resize';
    } else {
      handle.cursor = 'nesw-resize';
    }

    handle.on('pointerdown', e => {
      // For select mode, corner handles work as scale handles
      this.startDrag('xy', e);
    });

    return handle;
  }

  private createBoundingBoxLines() {
    const box = new PIXI.Graphics();
    const size = this.config.axisLength;
    box.rect(-size / 2, -size / 2, size, size);
    box.stroke({ color: this.COLOR_Z, width: 2, alpha: 0.5 });
    this.container.addChild(box);
  }

  // ==========================================
  // INTERACTION HANDLERS
  // ==========================================
  private setupInteraction() {
    this.container.eventMode = 'static';
    this.container.interactiveChildren = true;

    this.stage.eventMode = 'static';

    this.stage.on('pointermove', (event: any) => {
      if (!this.isDragging || !this.dragAxis) return;

      // ВАЖНО: Преобразуем экранные координаты в локальные координаты viewport
      // Это учитывает ВСЕ трансформации: contentContainer.scale + viewport.position + viewport.zoom
      const localPos = this.viewport.toLocal(event.global);
      const startLocalPos = this.viewport.toLocal(this.dragStartPos);

      let deltaX = localPos.x - startLocalPos.x;
      let deltaY = localPos.y - startLocalPos.y;

      // Constrain to axis
      if (this.dragAxis === 'x') {
        deltaY = 0;
      } else if (this.dragAxis === 'y') {
        deltaX = 0;
      }

      this.handleDrag(deltaX, deltaY, { x: event.global.x, y: event.global.y });
    });

    this.stage.on('pointerup', () => {
      if (this.isDragging) {
        logger.info('🎯 Gizmo drag ended', {
          source: 'transform-gizmo',
          axis: this.dragAxis,
          mode: this.mode,
        });
        this.isDragging = false;
        this.dragAxis = null;
      }
    });

    this.stage.on('pointerupoutside', () => {
      if (this.isDragging) {
        logger.info('🎯 Gizmo drag cancelled (outside)', {
          source: 'transform-gizmo',
          axis: this.dragAxis,
          mode: this.mode,
        });
        this.isDragging = false;
        this.dragAxis = null;
      }
    });
  }

  private startDrag(axis: 'x' | 'y' | 'xy' | 'rotate', event: any) {
    this.isDragging = true;
    this.dragAxis = axis;

    // Сохраняем начальную позицию в screen coordinates для правильного расчета delta
    this.dragStartPos = {
      x: event.global.x,
      y: event.global.y,
    };

    logger.info('🎯 Gizmo drag started', {
      source: 'transform-gizmo',
      axis,
      mode: this.mode,
      startPos: this.dragStartPos,
    });

    event.stopPropagation();
  }

  private handleDrag(deltaX: number, deltaY: number, currentPos?: { x: number; y: number }) {
    if (!this.onTransform) return;

    const data = {
      deltaX,
      deltaY,
      axis: this.dragAxis,
    };

    switch (this.mode) {
      case 'move':
        this.onTransform('move', data);
        break;
      case 'scale':
        this.onTransform('scale', {
          ...data,
          distance: Math.sqrt(deltaX * deltaX + deltaY * deltaY),
        });
        break;
      case 'rotate':
        // Calculate angle from center
        const angle = Math.atan2(deltaY, deltaX);
        this.onTransform('rotate', { ...data, angle });
        break;
    }

    // ВАЖНО: Обновляем startPos после каждого drag для incremental updates
    if (currentPos) {
      this.dragStartPos = currentPos;
    }
  }

  private highlightAxis(axis: PIXI.Container, color: number) {
    const graphics = axis.children[0] as PIXI.Graphics;
    if (!graphics) return;

    graphics.clear();

    // Redraw with new color
    graphics.moveTo(0, 0);
    graphics.lineTo(this.config.axisLength, 0);
    graphics.stroke({ color, width: this.config.axisWidth });

    // Arrow head
    const headSize = this.config.handleSize;
    graphics.moveTo(this.config.axisLength, 0);
    graphics.lineTo(this.config.axisLength - headSize, -headSize / 2);
    graphics.lineTo(this.config.axisLength - headSize, headSize / 2);
    graphics.lineTo(this.config.axisLength, 0);
    graphics.fill({ color });
  }

  destroy() {
    // Remove stage listeners
    this.stage.off('pointermove');
    this.stage.off('pointerup');
    this.stage.off('pointerupoutside');

    // Clear all gizmos
    this.clearGizmo();

    // Remove container listeners
    this.container.removeAllListeners();
    this.container.destroy();
  }
}
